"use client";
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { PayPalButton } from "@/components/paypal-button";
import { env } from "@/env.mjs";
import { useRouter } from "next/navigation";
import { Session } from "next-auth";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogTrigger,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import TrackPageView from "@/lib/page-view-tracker";
import Link from "next/link";
import CircularProgressIndicator from "@/components/circular-progress-indicator";

type Props = {
	session: Session | null;
};

interface PricingOptionData {
	title: string;
	description: string;
	features: string[];
	free: boolean;
	cost?: string;
	planId?: string;
}

const pricingOptions: PricingOptionData[] = [
	{
		title: "Free",
		description: "",
		features: [
			"Promoters\n8 months to promote businesses/services for FREE with one uploaded self-introduction video.",
			"Teachers\n8 months to sell skills for FREE with one uploaded self-introduction video.",
			"Post Comments",
			"Learn Skills From Teachers",
			"24/7 Support",
		],
		free: true,
	},
	{
		title: "Promoter",
		description: "Access additional features",
		features: [
			"24/7 Support",
			"Advertise your business or products",
			"Upload up to 10 videos",
		],
		free: false,
		cost: "3.99",
		planId: env.NEXT_PUBLIC_PLAN_ID_PROMOTER,
	},
	{
		title: "Teacher",
		description: "Access premium features",
		features: [
			"24/7 Support",
			"Advertise your business or products",
			"Upload up to 10 videos",
			"Sell Skill Online",
		],
		free: false,
		cost: "3.99",
		planId: env.NEXT_PUBLIC_PLAN_ID_SKILLS,
	},
	{
		title: "TeacherPlus",
		description: "Access all features",
		features: [
			"Watch Video Clips",
			"24/7 Support",
			"Advertise your business or products",
			"Upload up to 10 videos",
			"Sell 5 Skills Online",
		],
		free: false,
		cost: "19.99",
		planId: env.NEXT_PUBLIC_PLAN_ID_SKILLS_PLUS,
	},
];

export default function MembershipPage({ session }: Props) {
	const router = useRouter();

	const [showFreeDialog, setShowFreeDialog] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const handleGetStarted = async (tier: string) => {
		//expire 8 months from date joined
		const expiry = new Date();
		expiry.setMonth(expiry.getMonth() + 8);

		await fetch("/api/user/profile", {
			method: "PATCH",
			body: JSON.stringify({
				membership: tier,
				trial: true,
				subId: uuidv4(),
			}),
		});

		setIsLoading(false);
		router.push(`/profile/${session?.user.id}`);
	};

	return (
		<>
			<TrackPageView />
			<div className="min-h-screen bg-gray-100 py-10">
				<div className="max-w-7xl mx-auto">
					<div className="mb-8 flex flex-col items-center justify-center">
						<div className="w-full flex items-center animate-none px-0 justify-center bg-gradient-to-br from-sky-300 to-blue-500 via-purple-300 rounded-md max-w-[580px] min-[568px]:h-[332px] min-[568px]:px-2 min-[618px]:px-0 min-[568px]:animate-gradient-xy">
							<iframe
								className="w-full h-80 rounded-none max-w-[568px] min-[568px]:rounded-md"
								src={
									"https://www.youtube.com/embed/VDEpanm6tR4?autoplay=1&mute=1"
								}
								title={"Steps"}
								frameBorder="0"
								allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
								allowFullScreen></iframe>
						</div>
					</div>

					<div className="px-4 sm:px-6 lg:px-8">
						<h1 className="text-4xl font-bold text-center mb-8">
							Pricing Options
						</h1>

						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
							<PayPalScriptProvider
								options={{
									clientId: env.NEXT_PUBLIC_PAYPAL_CLIENT_ID,
									intent: "subscription",
									vault: true,
								}}>
								{pricingOptions.map((option) => (
									<div
										key={option.title}
										className={`"bg-white rounded-lg p-8 relative " ${
											option.free
												? "bg-slate-950 shadow-[10px_10px_0px_1px_#5fe385]"
												: "shadow-lg"
										}`}>
										{option.free && (
											<div className="rounded-full w-2 h-2 bg-[#5fe385] ring-4 ring-[#389653] animate-pulse"></div>
										)}

										<h2
											className={`text-2xl font-bold pt-3 text-gray-800 mb-4 ${
												option.free
													? "uppercase text-4xl text-center !text-[#5fe385]"
													: ""
											}`}>
											{option.title}
										</h2>

										{option?.cost && (
											<span className="flex align-baseline gap-2 justify-center items-baseline text-blue-500 text-center font-semibold">
												<h1 className="text-2xl">${option.cost}</h1>
												<h2>USD/yr</h2>
											</span>
										)}

										<p className={"text-lg mb-6 text-gray-600"}>
											{option.description}
										</p>
										<ul
											className={
												option.free
													? "text-gray-300"
													: "text-gray-600"
											}>
											{option.features.map((feature, index) => (
												<li
													className="flex items-baseline mb-3 whitespace-pre-line"
													key={index}>
													<svg
														className="w-4 h-4 flex-shrink-0 text-green-500 mr-2"
														viewBox="0 0 24 24"
														fill="none"
														xmlns="http://www.w3.org/2000/svg">
														<path
															d="M5 13L9 17L19 7"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
													{feature}
												</li>
											))}
										</ul>
										{!option.free ? (
											<PayPalButton
												planId={option.planId!}
												tier={option.title}
												userId={session?.user.id}
											/>
										) : (
											<Dialog
												open={showFreeDialog}
												onOpenChange={setShowFreeDialog}>
												<DialogTrigger asChild>
													<Button className="w-full bg-[#5fe385] text-white font-bold py-2 px-4 rounded mt-6 hover:bg-white hover:text-slate-950 transition-all duration-200">
														{isLoading ? (
															<CircularProgressIndicator />
														) : (
															"Get Started"
														)}
													</Button>
												</DialogTrigger>

												<DialogContent className="flex flex-col justify-center items-center">
													<DialogHeader className="text-center sm:text-center">
														<DialogTitle>Free Trial</DialogTitle>
														<DialogDescription>
															Which type of membership would you
															like to select for your 8 month
															free trial?
														</DialogDescription>
													</DialogHeader>

													<div className="flex gap-4 items-center justify-center">
														<Button
															onClick={() => {
																handleGetStarted("Promoter");
																setShowFreeDialog(false);
																setIsLoading(true);
															}}
															className="w-full text-white font-bold py-2 px-4 rounded mt-6 max-w-[350px] bg-gradient-to-br to-violet-300 from-violet-400 hover:to-[#5fe385] hover:from-[#5fe385]">
															Promoter
														</Button>

														<Button
															onClick={() => {
																handleGetStarted("Teacher");
																setShowFreeDialog(false);
																setIsLoading(true);
															}}
															className="w-full text-white font-bold py-2 px-4 rounded mt-6 max-w-[350px] bg-gradient-to-br to-sky-400 from-blue-500 hover:to-[#5fe385] hover:from-[#5fe385]">
															Teacher
														</Button>
													</div>
												</DialogContent>
											</Dialog>
										)}
									</div>
								))}
							</PayPalScriptProvider>
						</div>

						<div className="flex items-center justify-center text-center my-4">
							<Link
								className="overflow-hidden transition-all relative flex text-gray-900 bg-[#F7BE38] font-medium rounded-full px-5 py-2.5 origin-top-left text-center items-center sm:flex-row hover:text-blue-400 me-2 mb-2 group"
								href="https://www.paypal.com/us/digital-wallet/send-receive-money"
								target="_blank">
								<span className="w-0 h-[9.3rem] origin-top-left rounded-full bg-[#1a1a1a] absolute top-[-3rem] left-0 ease-out duration-500 transition-all pointer-events-none group-hover:w-full"></span>

								<svg
									className="w-4 h-4 me-2 z-[2] -ms-1 hidden min-[517px]:block"
									aria-hidden="true"
									focusable="false"
									data-prefix="fab"
									data-icon="paypal"
									role="img"
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 384 512">
									<path
										fill="currentColor"
										d="M111.4 295.9c-3.5 19.2-17.4 108.7-21.5 134-.3 1.8-1 2.5-3 2.5H12.3c-7.6 0-13.1-6.6-12.1-13.9L58.8 46.6c1.5-9.6 10.1-16.9 20-16.9 152.3 0 165.1-3.7 204 11.4 60.1 23.3 65.6 79.5 44 140.3-21.5 62.6-72.5 89.5-140.1 90.3-43.4 .7-69.5-7-75.3 24.2zM357.1 152c-1.8-1.3-2.5-1.8-3 1.3-2 11.4-5.1 22.5-8.8 33.6-39.9 113.8-150.5 103.9-204.5 103.9-6.1 0-10.1 3.3-10.9 9.4-22.6 140.4-27.1 169.7-27.1 169.7-1 7.1 3.5 12.9 10.6 12.9h63.5c8.6 0 15.7-6.3 17.4-14.9 .7-5.4-1.1 6.1 14.4-91.3 4.6-22 14.3-19.7 29.3-19.7 71 0 126.4-28.8 142.9-112.3 6.5-34.8 4.6-71.4-23.8-92.6z"></path>
								</svg>

								<span className="z-[2]">
									Don&apos;t have a PayPal account? Click here to get
									started!
								</span>
							</Link>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
